/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Button,
  Col,
  Row,
  Table,
  Label,
  Card,
  CardBody,
  Input,
} from 'reactstrap';

import { CompanyId } from '../../constants';
import LocalstorageService from '../../helpers/localstorage-service';
import { setLoadingOverlay } from '../../store/slices/loadingOverlaySlice';
import reportService from '../../services/report';
import { exportToExcel, exportToPDF } from '../../helpers/exportUtils';
import NoRecords from '../../components/Table/NoRecords';

const CheckFlowReport = () => {
  const dispatch = useDispatch();
  const [setPageTitle, setCrumbItem] = useOutletContext();

  const companyId = LocalstorageService.getItem(CompanyId);
  const today = new Date();
  const threeMonthsAgo = new Date(today);
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  const fmtDate = (d) => d.toISOString().slice(0, 10);

  // ── Filters ──
  const [fromDate,  setFromDate]  = useState(fmtDate(threeMonthsAgo));
  const [toDate,    setToDate]    = useState(fmtDate(today));
  const [reference, setReference] = useState('');

  // ── Results ──
  const [reportData, setReportData] = useState([]);
  const [totals,     setTotals]     = useState(null);
  const [isLoading,  setIsLoading]  = useState(false);

  useEffect(() => {
    setPageTitle('דוח זרימת כסף');
    setCrumbItem([
      { title: 'ATM',    link: '#' },
      { title: 'דוחות', link: '#' },
      { title: 'זרימת כסף', link: '#' },
    ]);
  }, []);

  const generateReport = async () => {
    setIsLoading(true);
    dispatch(setLoadingOverlay(true));
    try {
      const payload = { companyId, fromDate, toDate, reference };
      const response = await reportService.getCheckFlowReport(payload);
      const { status, dataList, totalWithdrawn, totalReturned, totalPending, totalExpenses, totalGap } = response.data;

      if (status === 200) {
        setReportData(dataList || []);
        setTotals({ totalWithdrawn, totalReturned, totalPending, totalExpenses, totalGap });
        if (!dataList?.length) toast.info('לא נמצאו נתונים לפי הסינון שנבחר');
      } else {
        toast.error('שגיאה בטעינת הדוח');
      }
    } catch (e) {
      console.error('CheckFlowReport error:', e);
      toast.error('שגיאה בטעינת הדוח');
    } finally {
      setIsLoading(false);
      dispatch(setLoadingOverlay(false));
    }
  };

  const resetFilters = () => {
    setFromDate(fmtDate(threeMonthsAgo));
    setToDate(fmtDate(today));
    setReference('');
    setReportData([]);
    setTotals(null);
  };

  const formatNum = (n) =>
    new Intl.NumberFormat('he-IL', { maximumFractionDigits: 0 }).format(n ?? 0);

  const columns = [
    { key: 'rowNum',             header: '#' },
    { key: 'withdrawalDate',     header: 'תאריך' },
    { key: 'reference',          header: 'אסמכתה' },
    { key: 'employeeNames',      header: 'עובדים' },
    { key: 'terminalNumbers',    header: 'מסופים' },
    { key: 'withdrawnAmount',    header: 'נמשך (₪)' },
    { key: 'returnedAmount',     header: 'הוחזר (₪)' },
    { key: 'pendingTotalToReturn', header: 'בהמתנה (₪)' },
    { key: 'totalExpenses',      header: 'הוצאות (₪)' },
    { key: 'gap',                header: 'פער (₪)' },
    { key: 'status',             header: 'סטטוס' },
  ];

  const handleExportExcel = () => {
    if (!reportData.length) { toast.warning('אין נתונים לייצוא'); return; }
    const rows = reportData.map((r, i) => ({ ...r, rowNum: i + 1 }));
    exportToExcel(rows, columns, `CheckFlowReport_${fmtDate(today)}`, 'דוח זרימת כסף');
    toast.success('הקובץ יורד בהצלחה');
  };

  const handleExportPDF = () => {
    if (!reportData.length) { toast.warning('אין נתונים לייצוא'); return; }
    const rows = reportData.map((r, i) => ({ ...r, rowNum: i + 1 }));
    exportToPDF(rows, columns, `CheckFlowReport_${fmtDate(today)}`, 'דוח זרימת כסף');
  };

  const rowClass = (row) => {
    if (row.gap !== 0) return 'table-danger';      // פער — חריג
    if (row.status === 'פתוח') return 'table-warning'; // עדיין פתוח
    return 'table-success';                          // סגור ומאוזן
  };

  return (
    <div className="check-flow-report" dir="rtl">
      {/* ── Filters ── */}
      <Card className="mb-3">
        <CardBody>
          <h5 className="mb-3">🔍 סינון</h5>

          <Row className="mb-3">
            <Col md={3}>
              <Label>מתאריך</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </Col>
            <Col md={3}>
              <Label>עד תאריך</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </Col>
            <Col md={3}>
              <Label>אסמכתה (צ'ק / הפניה)</Label>
              <Input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="חיפוש חופשי..."
              />
            </Col>
          </Row>

          <Row>
            <Col className="d-flex justify-content-end gap-2">
              <Button color="secondary" outline onClick={resetFilters}>🔄 איפוס</Button>
              <Button color="primary" onClick={generateReport} disabled={isLoading}>
                📊 הפקת דוח
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* ── Results ── */}
      {reportData.length > 0 && (
        <Card>
          <CardBody>
            {/* Header row */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h5 className="mb-0">📋 תוצאות ({reportData.length} משיכות)</h5>
              <div className="d-flex align-items-center gap-2">
                <Button color="success" size="sm" onClick={handleExportExcel}>📥 Excel</Button>
                <Button color="danger"  size="sm" onClick={handleExportPDF}>📄 PDF</Button>
              </div>
            </div>

            {/* Totals summary bar */}
            {totals && (
              <div className="d-flex flex-wrap gap-2 mb-3">
                <span className="badge bg-primary fs-6">נמשך: {formatNum(totals.totalWithdrawn)} ₪</span>
                <span className="badge bg-success fs-6">הוחזר: {formatNum(totals.totalReturned)} ₪</span>
                <span className="badge bg-warning text-dark fs-6">בהמתנה: {formatNum(totals.totalPending)} ₪</span>
                <span className="badge bg-secondary fs-6">הוצאות: {formatNum(totals.totalExpenses)} ₪</span>
                {totals.totalGap !== 0
                  ? <span className="badge bg-danger fs-6">⚠️ פער: {formatNum(totals.totalGap)} ₪</span>
                  : <span className="badge bg-success fs-6">✅ מאוזן</span>
                }
              </div>
            )}

            <div className="table-responsive">
              <Table bordered hover size="sm">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>תאריך</th>
                    <th>אסמכתה</th>
                    <th>עובדים</th>
                    <th>מסופים</th>
                    <th className="text-end">נמשך ₪</th>
                    <th className="text-end">הוחזר ₪</th>
                    <th className="text-end">בהמתנה ₪</th>
                    <th className="text-end">הוצאות ₪</th>
                    <th className="text-end">פער ₪</th>
                    <th>סטטוס</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((row, idx) => (
                    <tr key={row.withdrawalId ?? idx} className={rowClass(row)}>
                      <td>{idx + 1}</td>
                      <td>{row.withdrawalDate}</td>
                      <td>{row.reference || '—'}</td>
                      <td>{row.employeeNames || '—'}</td>
                      <td style={{ maxWidth: 180, whiteSpace: 'normal', fontSize: '0.8em' }}>
                        {row.terminalNumbers || '—'}
                      </td>
                      <td className="text-end fw-bold">{formatNum(row.withdrawnAmount)}</td>
                      <td className="text-end">{formatNum(row.returnedAmount)}</td>
                      <td className="text-end">{formatNum(row.pendingTotalToReturn)}</td>
                      <td className="text-end">{row.totalExpenses > 0 ? formatNum(row.totalExpenses) : '—'}</td>
                      <td className="text-end fw-bold">
                        {row.gap !== 0
                          ? <span className="text-danger">{formatNum(row.gap)}</span>
                          : <span className="text-success">0</span>
                        }
                      </td>
                      <td>
                        <span className={`badge ${row.status === 'סגור' ? 'bg-success' : 'bg-warning text-dark'}`}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-secondary fw-bold">
                  <tr>
                    <td colSpan={5} className="text-end">סה"כ:</td>
                    <td className="text-end">{formatNum(totals?.totalWithdrawn)}</td>
                    <td className="text-end">{formatNum(totals?.totalReturned)}</td>
                    <td className="text-end">{formatNum(totals?.totalPending)}</td>
                    <td className="text-end">{formatNum(totals?.totalExpenses)}</td>
                    <td className="text-end">
                      {totals?.totalGap !== 0
                        ? <span className="text-danger">{formatNum(totals?.totalGap)}</span>
                        : <span className="text-success">0</span>
                      }
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </Table>
            </div>

            {/* Legend */}
            <div className="mt-2 d-flex flex-wrap gap-3 small text-muted">
              <span><span className="badge bg-success">■</span> סגור ומאוזן</span>
              <span><span className="badge bg-warning text-dark">■</span> פתוח (TotalToReturn פעיל)</span>
              <span><span className="badge bg-danger">■</span> פער (חריג — בדיקה נדרשת)</span>
            </div>
          </CardBody>
        </Card>
      )}

      {!isLoading && reportData.length === 0 && (
        <Card><CardBody><NoRecords /></CardBody></Card>
      )}
    </div>
  );
};

export default CheckFlowReport;
